import {
  Box,
  Dialog,
  Card,
  CardContent,
  Link,
  Chip,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
  IconButton,
  Button,
  Drawer,
  CircularProgress, useMediaQuery, SvgIcon, Tooltip
} from '@mui/material';
import { DatePicker } from '@mui/x-date-pickers';
import KeyboardArrowUpOutlinedIcon from '@mui/icons-material/KeyboardArrowUpOutlined';
import React, { useEffect, useRef, useState, useLayoutEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import TypeAhead from 'components/TypeAhead';
import { useAuth } from 'hooks/useAuthContext';
import { useSnackbar } from 'components/snackbar';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm, Controller } from 'react-hook-form';
import FormProvider from 'hooks/form/form-provider';
import { CandidateEducationSchema } from 'models/CandidateProfileSchema';
import { updateCandidateEducation } from 'api/users';
import dayjs from 'dayjs';
import DynamicTextField from 'components/DynamicTextField';
import { endpoints } from 'utils/axios';
import { allCapitalize } from 'utils/Utils';
import AddSkills from './AddSkills';
import RemoveEducation from './RemoveEducationDialog';
import { deleteEducationHistory } from 'api/users';
import Overlapping from './Overlapping';
import { useTheme } from '@mui/material/styles';
import { DeleteSvg, EditSvgIcon } from 'components/icon/icon';
import { useBackNavigationDialog } from 'hooks/useBackNavigationDialog';
import { certDefSkill } from 'data/language';

/**
 * Education details form component
 *
 * @param {object} educationData - The education data object
 * @param {string} id - The id of the education data
 * @param {function} onCancel - The function to call when cancel button is clicked
 * @param {function} onEducationDataChange - The function to call when education data changes
 * @param {array} degreeData - The degree data array
 * @param {function} checkingOverlapping - The function to call when overlapping is checked
 * @param {boolean} showEdu - The flag to show the education details form
 * @param {function} handleShowEdu - The function to call when education details form is shown
 * @param {function} handleDeleteEdu - The function to call when education is deleted
 * @param {function} handleArrowClick - The function to call when arrow is clicked
 * @param {number} educationLength - The length of education array
 * @param {boolean} isReadOnly - The flag to show the form as read only
 * @param {function} setReadOnly - The function to call when read only mode is changed
 */
const EducationDetails = ({ educationData, id, onCancel, onEducationDataChange, degreeData, checkingOverlapping, showEdu, handleShowEdu, handleDeleteEdu, handleArrowClick, educationLength, isReadOnly, setReadOnly }) => {
  const { enqueueSnackbar } = useSnackbar();
  const isEditMode = !!id;
  const navigate = useNavigate();
  const { user, updateUnsaved, updateContext } = useAuth();
  const [showFlag, setShowFlag] = useState('');
  const [state, setState] = React.useState(false);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [educationFrom, setEducationFrom] = useState(dayjs(educationData?.education_from) || null);
  // const [selectedChips, setSelectedChips] = useState(educationData?.skills || []);
  const [keyAccomplishments, setKeyAccomplishments] = useState(
    educationData?.key_accomplishments?.length > 0 ? [...educationData.key_accomplishments, ''] : ['']
  );
  const focusRef = useRef(null);
  const degreeInteractionFlag = useRef(false);
  // const [open, setOpen] = useState(false);
  // const [openRemove, setOpenRemove] = useState(false);
  // const [addMoreSkills, setAddMoreSkills] = useState(educationData?.all_skills || []);
  const [chipData, setChipData] = useState(educationData?.skills?.length > 2 ? educationData?.skills : educationData?.all_skills || []);
  const defaultValues = {
    education_from: null,
    education_to: null,
    education_institute_name: '',
    degree_received: '',
    learning_format: 'Full time - offline',
    field_of_study: '',
    skills: [],
    ...educationData,
  };
  const methods = useForm({
    defaultValues,
    mode: 'onChange',
    resolver: yupResolver(CandidateEducationSchema('degree', user?.date_of_birth)),
  });

  const {
    register,
    handleSubmit,
    control,
    getValues,
    setValue,
    reset,
    formState: { errors, isDirty, isValid, dirtyFields, isSubmitting },
    watch,
    trigger,
  } = methods;

  const watchFileds = watch();

  useLayoutEffect(() => {
    if (!isEditMode) focusRef.current?.focus()
  }, []);

  const [isChangeSkills, setIsChangeSkills] = useState(false);
  // const [openOverlapping, setOpenOverlapping] = useState(false);
  const [type, setType] = useState('');
  const [degree, setDegree] = useState('');
  const [newEducationData, setNewEducationData] = useState(null);

  /**
   * Checks if the provided data object has any meaningful values in specific fields.
   * The function returns true if the data is null or undefined, or if none of the specified
   * fields contain meaningful data.
   *
   * A field is considered meaningful if:
   * - It is a non-empty string.
   * - It is an array with at least one non-empty string item.
   *
   * The fields checked for meaningful data are:
   * - 'education_institute_name'
   * - 'degree_received'
   * - 'field_of_study'
   * - 'education_from'
   * - 'education_to'
   * - 'skills'
   * - 'key_accomplishments'
   *
   * @param {Object} data - The data object to check.
   * @returns {boolean} True if the data object is null, undefined, or lacks meaningful data in the specified fields.
   */

  const checkLocalDataIsEmpty = (data) => {
    if (!data) return true;

    const meaningfulFields = [
      'education_institute_name',
      'degree_received',
      'field_of_study',
      'education_from',
      'education_to',
      'skills',
      'key_accomplishments',
    ];

    const hasMeaningfulData = meaningfulFields.some(field => {
      const value = data[field];

      if (value === null || value === undefined) {
        return false;
      }

      if (Array.isArray(value)) {
        return value.filter(item => String(item).trim() !== '').length > 0;
      }

      if (typeof value === 'string') {
        return value.trim() !== '';
      }

      return true;
    });

    return !hasMeaningfulData;
  };

  useEffect(() => {
    if (isEditMode) return;

    const formValues = getValues();
    formValues.key_accomplishments = keyAccomplishments.filter(acc => acc.trim() !== '');

    if (checkLocalDataIsEmpty(formValues)) {
      localStorage.removeItem('education');
    } else {
      localStorage.setItem('education', JSON.stringify(formValues));
    }
  }, [keyAccomplishments]);

  useEffect(() => {
    if (isEditMode) return;
    const subscription = watch((formValues) => {
      if (checkLocalDataIsEmpty(formValues)) {
        localStorage.removeItem('education');
      } else {
        localStorage.setItem('education', JSON.stringify(formValues));
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, isEditMode]);

  const handleReset = (data) => {
    const dataToReset = { ...(data || defaultValues) };
    dataToReset.key_accomplishments = data?.key_accomplishments?.filter(acc => acc && acc.trim() !== '') || [];
    reset(dataToReset);
    setChipData(dataToReset?.skills?.length > 2 ? dataToReset?.skills : dataToReset?.all_skills || []);
  };

  useEffect(() => {
    if (isEditMode) {
      handleReset(educationData);
    } else {
      const savedData = JSON.parse(localStorage.getItem('education'));
      handleReset(savedData);
    }
    degreeInteractionFlag.current = false;
  }, [educationData, isEditMode]);

  /**
   * Handles the change event of the 'From' date input element.
   * If the user selects a date, it sets the value of 'education_from' to the first day of the selected month
   * and triggers the 'education_from' field validation.
   * @param {Date} date - the selected date
   */
  const handleEducationFromChange = async (date) => {
    if (date) {
      const firstDayOfMonth = date.startOf('month');
      setEducationFrom(firstDayOfMonth);
      setValue('education_from', firstDayOfMonth?.$d);
      trigger('education_from');
    }
  };

  const handleEducationtoChange = (date) => {
    if (date) {
      const lastDayOfMonth = date.endOf('month');
      setValue('education_to', lastDayOfMonth?.$d);
      trigger('education_to');
    }
  };

  const format = [{ label: 'Full time - offline' }, { label: 'Full time - online' }, { label: 'Part time - offline' }, { label: 'Part time - online' }, { label: 'Self Study' }];

  /**
   * Handles the change event of the accomplishment text input elements.
   * When the user types something in the last accomplishment text input element and presses Enter,
   * it adds a new empty text input element to the end of the accomplishments array.
   * If the user deletes the text in an accomplishment text input element and presses Enter,
   * it removes the empty text input element from the accomplishments array.
   * @param {number} index - The index of the accomplishment text input element that changed
   * @param {string} value - The new value of the accomplishment text input element
   */
  const handleAccomplishmentChange = (index, value) => {
    const newAccomplishments = [...keyAccomplishments];
    newAccomplishments[index] = value;
    if (index === newAccomplishments.length - 1 && value.trim() !== '') {
      newAccomplishments.push('');
    } else if (!value && newAccomplishments.length > 1 && index !== newAccomplishments.length - 1) {
      newAccomplishments.splice(index, 1);
    }
    setKeyAccomplishments(newAccomplishments);
  };

  const onSubmit = handleSubmit(async (formData) => {
    const finalFormData = { ...formData };
    if (isEditMode) finalFormData.educationId = id;
    if (!validateSkills(finalFormData.skills)) return;

    finalFormData.key_accomplishments = keyAccomplishments.filter(acc => acc.trim() !== '');

    const { isOverlapping, type, degree } = checkingOverlapping(finalFormData);

    if (isOverlapping) {
      if (!finalFormData.is_overlap) {
        finalFormData.is_overlap = true;
        setNewEducationData(finalFormData);
        setDegree(degree);
        setType(type);
        handleOpenOverlapping();
        return;
      }
    } else {
      finalFormData.is_overlap = false;
    }

    if (showEdu) {
      handleShowEdu(finalFormData, id);
      handleReset(defaultValues);
      setReadOnly(true);
      return;
    }
    await updateEducation(finalFormData);
  });

  /**
   * Updates a candidate's education record.
   * If the update is successful, it removes the education item from local storage if it was not an edit,
   * and calls the onEducationDataChange function with true as the argument.
   * If the update is unsuccessful, it shows an error message with the variant 'error'.
   * @param {object} data - The education data to update
   */
  const updateEducation = async (data) => {
    if (data) {
      const response = await updateCandidateEducation(data);
      if (response?.success === true && response?.data) {
        const educationId = response?.data?._id;
        if (!user?.stages || user?.stages < 2) updateContext({ stages: 2 });
        if (response?.removedWorkGapResult)
          enqueueSnackbar("Career break has been removed.", { variant: 'success' });
        if (!isEditMode) {
          localStorage.removeItem('education');
        }
        if (isEditMode) onEducationDataChange(true, educationId);
        else onEducationDataChange(true);
        onCancel();
      } else if (response?.success === false) {
        enqueueSnackbar(response.message, { variant: 'error' });
      }
    }
  };

  /**
   * Toggles the selection of a skill label. 
   * If the label is already selected, it removes it from the selected skills list.
   * If the label is not selected and the number of selected skills is less than 8, it adds the label to the list.
   * If the number of selected skills is 8 or more, it sets a flag to show an error message.
   * Also triggers validation and updates the skills input field.
   * @param {string} label - The label of the skill to be toggled.
   */

  const handleClick = (label) => {
    const currentSkills = getValues('skills') || [];
    let newSkills;
    if (currentSkills.includes(label)) {
      newSkills = currentSkills.filter((chip) => chip !== label);
    } else {
      if (currentSkills.length >= 8) {
        setShowFlag('You can select up to 8 skills.');
        return;
      }
      newSkills = [...currentSkills, label];
    }
    setValue("skills", newSkills, { shouldDirty: true });
    trigger("skills");
    validateSkills(newSkills);
  };

  const validateSkills = (totalSkillls) => {
    if (totalSkillls?.length < 3) {
      setShowFlag('Please select at least 3 skills');
      return false;
    } else if (totalSkillls?.length > 8) {
      setShowFlag('You can select up to 8 skills.');
      return false;
    }
    setShowFlag('');
    return true;
  };

  /**
   * Opens the OTP verification dialog.
   * @function
   */
  // const handleOpenOtpDialog = () => {
  //   setOpen(true);
  // };

  /**
   * Closes the dialogs by setting their open states to false.
   * Specifically closes the main and remove dialogs.
   */

  // const handleClose = () => {
  //   // setOpen(false);
  //   setOpenRemove(false);
  // };

  /**
   * Validates and updates the selected skills with a new set of skills.
   * If the new skills are valid and not empty, it marks skills as changed,
   * updates the selected chips and chip data, sets the skills form value,
   * and triggers the validation for skills.
   * @param {Array<string>} moreSkills - The new set of skills to update.
   */

  const handleAddMoreSkills = (moreSkills) => {
    validateSkills(moreSkills);
    if (moreSkills?.length > 0) {
      setValue("skills", moreSkills, { shouldDirty: true });
      setValue("all_skills", moreSkills, { shouldDirty: true });
      trigger("skills");
      setChipData(moreSkills);
    }
  };

  const handleCollapse = () => {
    if (isEditMode) {
      handleReset(educationData);
    }
    handleArrowClick();
  };

  /**
   * Handles the cancel action for the education form.
   * If there is no ID, it removes the 'education' entry from local storage.
   * If 'showEdu' is true, it sets the form to read-only mode.
   * Finally, it calls the onCancel function to perform any additional cancellation logic.
   */

  const handleCancel = () => {
    if (isEditMode) {
      handleReset(educationData);
    } else {
      localStorage.removeItem('education');
    }
    if (showEdu) setReadOnly(true);
    onCancel();
  };

  /**
   * Handles the delete action for the education form.
   * If there is no ID, it removes the 'education' entry from local storage
   * and calls the onCancel function to perform any additional cancellation logic.
   * If the user is in stage 3 or later and there is only one education entry,
   * it shows an error message instead.
   * If the education ID exists, it opens the remove confirmation dialog.
   * @param {string} educationId - The ID of the education entry to delete.
   * @param {string} title - The title of the education entry to delete.
   * @param {Event} event - The event that triggered the delete action.
   */
  const handleDeleteEducation = async (educationId, title, event) => {
    event.stopPropagation();
    if (user?.stages >= 3 && educationLength === 1) {
      enqueueSnackbar('Education cannot be empty', { variant: 'error' });
      return;
    }
    if (!educationId) {
      localStorage.removeItem('education');
      onCancel();
    } else {
      handleOpenRemove();
    }
  };

  /**
   * Handles the delete action for the education form.
   * If showEdu is true, it calls the handleDeleteEdu function and closes the dialog.
   * If showEdu is false, it calls the deleteEducationHistory function and closes the dialog if the response is successful.
   * If the response is not successful, it shows an error message.
   */
  const handleDeleteAction = async () => {
    if (showEdu) {
      handleDeleteEdu(educationData?._id);
      handleCloseRemove();
      return;
    }
    const response = await deleteEducationHistory({ educationId: educationData?._id });
    if (response && response?.success) {
      onEducationDataChange(true);
    } else {
      enqueueSnackbar(response.message, { variant: 'error' });
    }
    handleCloseRemove();
  };

  /**
   * Closes the overlapping dialog box by setting its open state to false.
   */

  // const handleCloseOverlapping = () => {
  //   setOpenOverlapping(false);
  // };

  /**
   * Handles the confirmation of the add or edit education dialog.
   * If showEdu is true, calls the handleShowEdu function and sets the read-only flag to true.
   * If showEdu is false, calls the updateEducation function with the new education data.
   * @param {Object} newEducationData - The new education data to be saved.
   */

  const handleConfirm = async (newEducationData) => {
    if (showEdu) {
      handleShowEdu(newEducationData, id);
      setReadOnly(true);
      return;
    }
    await updateEducation(newEducationData);
  };

  const { openDialog, closeDialog, dialogLocation } = useBackNavigationDialog();
  const dialogIds = dialogLocation.state?.dialogIds || [];

  const openSkillDrawer = dialogIds.includes("moreSkills");
  const openOverlapping = dialogIds.includes("overlapping");
  const openRemove = dialogIds.includes("removeExpandEducation");

  const handleCloseSkillDrawer = () => {
    closeDialog('moreSkills');
  };
  const handleOpenSkillDrawer = () => {
    openDialog('moreSkills');
  };
  const handleCloseOverlapping = () => {
    closeDialog('overlapping');
  };
  const handleOpenOverlapping = () => {
    openDialog('overlapping');
  };
  const handleCloseRemove = () => {
    closeDialog('removeExpandEducation');
  };
  const handleOpenRemove = () => {
    openDialog('removeExpandEducation');
  };

  if (!degreeData && !degreeData?.length > 0) {
    return (
      <div key="add_edit_edu">
        <Box className="candidate-basic-det-wrapper"></Box>
        <Card className="form-wrapper" variant="outlined">
          <CardContent>
            <Stack direction="row" justifyContent={'space-between'} alignItems={'center'} mb={3}>
              <CircularProgress />
            </Stack>
          </CardContent>
        </Card>
      </div>
    );
  }
  return (
    <Box className="candidate-basic-det-wrapper education-details-form">
      <FormProvider methods={methods} onSubmit={onSubmit}>
        <Tooltip title={isReadOnly ? "Any information updated after unlocking will be reflected across resumes" : ''} arrow followCursor placement='top'>
          <Box className="job-update-form-top" mb={1}>
            {!isMobile && <Box className="details-info-header" mb={2.5}>
              <Stack direction="row" alignItems={'center'} justifyContent={'space-between'} spacing={2}>
                <Box>
                  <Stack direction="row" alignItems={'center'} spacing={1.5}>
                    <Typography variant="h6" className="card-header-title">
                      {watch('degree_received') ? `${watch('degree_received')} ` + `at ` : ''}
                      {watch('education_institute_name') ? allCapitalize(watch('education_institute_name')) : 'Education Details'}
                    </Typography>
                  </Stack>
                </Box>
                <Stack direction="row" alignItems={'center'} spacing={2} >
                  {(educationData && !isMobile) &&
                    (!isReadOnly ? <IconButton
                      className="delete-sec-btn"
                      aria-label="delete"
                      onClick={(event) =>
                        handleDeleteEducation(educationData?._id, educationData?.degree_received, event)
                      }
                    >
                      <DeleteSvg />
                    </IconButton> :
                      <IconButton aria-label="delete" sx={{ padding: 0, marginRight: 1 }} className="edit-icon" disableRipple onClick={() => setReadOnly(false)}>
                        <EditSvgIcon />
                      </IconButton>)
                  }
                  {!isMobile && <KeyboardArrowUpOutlinedIcon onClick={handleCollapse} style={{ cursor: 'pointer' }} />}
                </Stack>
              </Stack>
            </Box>}
            <Box className="details-info-content">
              <Grid container spacing={2} mb={3}>
                <Grid item xs={12} md={6}>
                  {/* institute */}
                  <FormControl fullWidth size="small">
                    <Controller
                      control={control}
                      name="education_institute_name"
                      render={({ field }) => (
                        <TypeAhead
                          {...field}
                          ref={focusRef}
                          api={`${process.env.REACT_APP_END_POINT}${endpoints?.services?.institute}`}
                          multiple={false}
                          fullWidth
                          label="Institution"
                          placeholder="Institution Name"
                          freeSolo={true}
                          displayTextKey="display_name"
                          onInputChange={(e, value) => field.onChange(value)}
                          disabled={isReadOnly}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  {/* degree_received */}
                  <FormControl fullWidth size="small">
                    <Controller
                      control={control}
                      name="degree_received"
                      render={({ field }) => (
                        <TypeAhead
                          {...field}
                          data={degreeData}
                          label="Degree"
                          displayTextKey="display_name"
                          disableClearable={true}
                          freeSolo={true}
                          onInputChange={(e, value) => {
                            field.onChange(value)
                            if (!degreeInteractionFlag.current && value) {
                              setValue('all_skills', certDefSkill, { shouldDirty: true });
                              setChipData(certDefSkill);
                              degreeInteractionFlag.current = true;
                            }
                          }}
                          onChange={(e, value) => {
                            field.onChange(value?.display_name || value);
                            const newAvailableSkills = value?.skills || certDefSkill;
                            setValue('all_skills', newAvailableSkills, { shouldDirty: true });
                            setChipData(newAvailableSkills);
                            setValue("skills", [], { shouldDirty: true });
                            trigger("skills");
                          }}
                          disabled={isReadOnly}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  {/* Specialization */}
                  <FormControl fullWidth size="small">
                    <Controller
                      control={control}
                      name="field_of_study"
                      defaultValue={defaultValues?.field_of_study || []}
                      render={({ field }) => (
                        <TextField
                          size="small"
                          {...field}
                          label="Specialization"
                          variant="outlined"
                          fullWidth
                          error={!!errors.field_of_study}
                          helperText={errors.field_of_study ? errors.field_of_study.message : ''}
                          disabled={isReadOnly}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  {/* learning format */}
                  <FormControl fullWidth size="small">
                    <InputLabel id="learning_format">Learning Format</InputLabel>
                    <Controller
                      control={control}
                      name="learning_format"
                      defaultValue={defaultValues?.learning_format || ''}
                      render={({ field }) => (
                        <Select
                          {...field}
                          labelId="learning_format"
                          error={!!errors.learning_format}
                          helperText={errors.learning_format?.message}
                          label="Learning Format"
                          variant="outlined"
                          fullWidth
                          disabled={isReadOnly}
                        >
                          {format?.map((item) =>
                            <MenuItem value={item?.label}>{item?.label}</MenuItem>
                          )}
                        </Select>
                      )}
                    />
                    <Typography variant="caption" color="error">
                      {errors.learning_format?.message}
                    </Typography>
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  {/* start date */}
                  <FormControl fullWidth>
                    <Controller
                      control={control}
                      name="education_from"
                      defaultValue={defaultValues?.education_from}
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          value={dayjs(field?.value)}
                          autoOk={true}
                          format="MM/YYYY"
                          views={['year', 'month']}
                          label="Start Date"
                          disableFuture
                          closeOnSelect={true}
                          sx={{ width: '100%' }}
                          onChange={(date) => handleEducationFromChange(date)}
                          slotProps={{
                            textField: {
                              size: 'small',
                              error: !!errors?.education_from,
                              helperText: errors?.education_from?.message,
                            },
                          }}
                          disabled={isReadOnly}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  {/* end date */}
                  <FormControl fullWidth>
                    <Controller
                      control={control}
                      name="education_to"
                      defaultValue={defaultValues?.education_to}
                      render={({ field }) => (
                        <DatePicker
                          {...field}
                          value={dayjs(field?.value)}
                          autoOk={true}
                          format="MM/YYYY"
                          views={['year', 'month']}
                          label="End Date"
                          onChange={(date) => handleEducationtoChange(date)}
                          slotProps={{
                            textField: {
                              size: 'small',
                              error: !!errors?.education_to,
                              helperText: errors?.education_to?.message,
                            },
                          }}
                          disableFuture
                          closeOnSelect={true}
                          disabled={isReadOnly}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
              </Grid>
              <Box className="besic-det-skills-wrapper">
                <Box mb={2}>
                  <Typography className="title" variant="body2" gutterBottom>
                    Skills
                  </Typography>
                  <Typography className="info-txt" variant="body2" gutterBottom>
                    Minimum 3 and maximum 8 skills to be selected
                  </Typography>

                </Box>
                <Stack direction="row" flexWrap="wrap">
                  {chipData?.map((label, index) => {
                    const selectedSkills = getValues('skills') || [];
                    const normalizedSkillsSet = new Set(
                      selectedSkills.map(skill => skill.toLowerCase())
                    );
                    const isSelected = normalizedSkillsSet.has(label.toLowerCase());
                    return (
                      <Chip
                        key={index}
                        label={label}
                        size="small"
                        clickable
                        variant={isSelected ? 'filled' : 'outlined'}
                        style={{
                          color: isSelected ? 'white' : 'black',
                          backgroundColor: isSelected ? '#ade9ab' : 'transparent',
                          border: isSelected ? 'none' : '1px solid rgba(0, 0, 0, 0.23)',
                        }}
                        onClick={() => handleClick(label)}
                        tabIndex={-1}
                        disabled={isReadOnly}
                      />
                    );
                  })}
                </Stack>
                {(errors?.skills || showFlag) && (
                  <Typography variant="caption" color="error" style={{ color: 'red', fontSize: 'small' }}>
                    {errors.skills?.message || showFlag}<br />
                  </Typography>
                )}
                {!isReadOnly && <Link className="add-more-skills-link" underline="none" size="small" onClick={handleOpenSkillDrawer} tabIndex={0}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleOpenSkillDrawer();
                    }
                  }}
                  disabled={isReadOnly}
                >
                  Add More Skills
                </Link>}
              </Box>
            </Box>
          </Box>
        </Tooltip>

        <Box className={isReadOnly ? "job-update-form-bottom" : "job-update-form-top"}>
          <Box className="accomplishments">
            <Typography className="title" variant="body2" gutterBottom>
              Accomplishments
            </Typography>
            <Typography className="info-txt" variant="body2" gutterBottom>
              Well structured accomplishments help recruiters get visibility on your strengths
            </Typography>
            {keyAccomplishments?.map((accomplishment, index) => (
              <Box key={index} mt={2}>
                <FormControl fullWidth>
                  <DynamicTextField
                    label="Key Accomplishments"
                    rowsMax={10}
                    value={accomplishment}
                    onChange={(value) => handleAccomplishmentChange(index, value)}
                  />
                </FormControl>
              </Box>
            ))}
          </Box>
        </Box>
        <Box className="job-update-form-top">
          <Box className="details-info-header" mb={isReadOnly && 2.5}>
            <Box className="form-action-btn">
              <Stack direction="row" spacing={{ xs: 0, md: 2 }} mt={{ xs: 0, md: isReadOnly ? 0 : 3 }}>
                <Button variant="text" className="cancel-btn" size="medium" sx={{ display: { xs: 'none', md: 'block' } }} onClick={handleCancel}>
                  Cancel
                </Button>
                <Button className="done-btn" variant="contained" disabled={isSubmitting} type="submit" size="medium" >
                  {isEditMode ? (isSubmitting ? 'Updating...' : 'Done') : (isSubmitting ? 'Saving...' : 'Done')}
                </Button>
              </Stack>
            </Box>
          </Box>
        </Box>
        {isMobile ?
          <Drawer
            anchor="bottom"
            open={openSkillDrawer}
            onClose={handleCloseSkillDrawer}
            className="forgot-password-dialog otp-dialog-wrapper"
          >
            <AddSkills
              onClose={handleCloseSkillDrawer}
              skills={getValues('all_skills')}
              selectedskills={getValues('skills')}
              onAddSkills={handleAddMoreSkills}
            />

          </Drawer> :
          <Dialog
            open={openSkillDrawer}
            onClose={handleCloseSkillDrawer}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="otp-dialog-wrapper"
          >
            <AddSkills
              onClose={handleCloseSkillDrawer}
              skills={getValues('all_skills')}
              selectedskills={getValues('skills')}
              onAddSkills={handleAddMoreSkills}
            />
          </Dialog>}

        <Dialog
          open={openRemove}
          onClose={handleCloseRemove}
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          className="otp-dialog-wrapper"
        >
          <RemoveEducation onClose={handleCloseRemove} onRemove={handleDeleteAction} title={educationData?.degree_received} />
        </Dialog>
        {isMobile ?
          <Drawer
            anchor="bottom"
            open={openOverlapping}
            onClose={handleCloseOverlapping}
            className="forgot-password-dialog career-breaks-dialog"
          >
            <Overlapping onClose={() => handleCloseOverlapping()}
              onConfirm={() => handleConfirm(newEducationData)}
              type={type}
              degree={degree} />
          </Drawer> :
          <Dialog
            open={openOverlapping}
            onClose={handleCloseOverlapping}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className="otp-dialog-wrapper"
          >
            <Overlapping onClose={() => handleCloseOverlapping()}
              onConfirm={() => handleConfirm(newEducationData)}
              type={type}
              degree={degree} />
          </Dialog>}
      </FormProvider>
    </Box >
  );
};

export default EducationDetails;
